import { useCallback, useRef, useState } from 'react';
import { uploadFileToS3 } from '@/shared/utils/uploadFileToS3';
import {
  COMPLETE_SR_LABELS as C,
  UPDATE_SR_STATUS_LABELS as L,
} from '../constants/labels';
import { UPDATE_SERVICE_REQUEST_STATUS_ERROR_MESSAGES } from '../errors';
import {
  generateServiceRequestAttachmentPresignedUrls,
  type ServiceRequestAttachmentMedia,
} from '../api/serviceRequestPresignedUrlsApiService';
import { updateServiceRequestStatus } from '../api/updateServiceRequestStatusApiService';

interface UseCompleteServiceRequestOptions {
  serviceRequestId: string | undefined;
  /** Fired after a successful completion so the caller can refetch + snackbar. */
  onSuccess?: () => void;
}

export interface UseCompleteServiceRequestReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;

  // File selection — files are held locally and only uploaded to S3 on submit.
  files: File[];
  onFilesChange: (files: File[]) => void;

  /** Single toast channel for file-validation, the notes/attachment rule, and upload failures. */
  toastMessage: string;
  showToast: (message: string) => void;
  clearToast: () => void;

  // Admin notes
  adminNotes: string;
  setAdminNotes: (value: string) => void;
  adminNotesError: string;

  // Submit
  isSubmitting: boolean;
  submitError: string;
  submit: () => Promise<void>;
}

/** S3 object key format for a completion attachment. */
const buildAttachmentPath = (serviceRequestId: string, filename: string) =>
  `service-requests/${serviceRequestId}/attachments/${crypto.randomUUID()}/${filename}`;

/** Pull the `{uuid}` segment out of an attachment path for a resourceId fallback. */
const resourceIdFromPath = (path: string): string => path.split('/')[3] ?? '';

export const useCompleteServiceRequest = ({
  serviceRequestId,
  onSuccess,
}: UseCompleteServiceRequestOptions): UseCompleteServiceRequestReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [toastMessage, setToastMessage] = useState('');

  const [adminNotes, setAdminNotesState] = useState('');
  const [adminNotesError, setAdminNotesError] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [submitError, setSubmitError] = useState('');

  const resetForm = useCallback(() => {
    setFiles([]);
    setToastMessage('');
    setAdminNotesState('');
    setAdminNotesError('');
    setSubmitError('');
  }, []);

  const open = useCallback(() => {
    resetForm();
    setIsOpen(true);
  }, [resetForm]);

  const close = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    setIsOpen(false);
    resetForm();
  }, [isSubmitting, resetForm]);

  const showToast = useCallback((message: string) => {
    setToastMessage(message);
  }, []);
  const clearToast = useCallback(() => setToastMessage(''), []);

  const onFilesChange = useCallback((nextFiles: File[]) => {
    // Files are only uploaded to S3 on submit — selecting just stages them.
    setFiles(nextFiles);
    setToastMessage('');
    setSubmitError('');
  }, []);

  const setAdminNotes = useCallback((value: string) => {
    setAdminNotesState(value);
    setAdminNotesError('');
    setToastMessage('');
  }, []);

  /**
   * Uploads every staged file to S3 via the provisional presigned-URL flow.
   * Returns the resolved media resources, or `null` if any upload failed.
   */
  const uploadAllFiles = useCallback(
    async (
      toUpload: File[]
    ): Promise<ServiceRequestAttachmentMedia[] | null> => {
      if (!serviceRequestId || toUpload.length === 0) {
        return [];
      }
      const paths = toUpload.map((f) =>
        buildAttachmentPath(serviceRequestId, f.name)
      );
      const presign =
        await generateServiceRequestAttachmentPresignedUrls(paths);
      if (!presign.success || !presign.data) {
        return null;
      }
      const entries = presign.data.files;
      const resources = await Promise.all(
        toUpload.map(async (file, idx) => {
          const entry = entries[idx];
          const path = paths[idx];
          if (!entry || !path) {
            return null;
          }
          try {
            await uploadFileToS3(entry.url, file);
            const resource: ServiceRequestAttachmentMedia = {
              isUploaded: true,
              path: entry.path,
              resourceId: entry.resourceId ?? resourceIdFromPath(path),
              filename: file.name,
              fileType: file.type,
              fileSize: file.size,
            };
            return resource;
          } catch {
            return null;
          }
        })
      );
      if (resources.some((r) => r === null)) {
        return null;
      }
      return resources as ServiceRequestAttachmentMedia[];
    },
    [serviceRequestId]
  );

  const submit = useCallback(async () => {
    if (!serviceRequestId || isSubmittingRef.current) {
      return;
    }

    const trimmedNotes = adminNotes.trim();
    // The button is always enabled; the notes-or-attachment rule is enforced
    // here and surfaced as a toast rather than blocking the click.
    if (trimmedNotes.length === 0 && files.length === 0) {
      setToastMessage(C.ERROR_NOTES_OR_ATTACHMENT_REQUIRED);
      return;
    }
    if (trimmedNotes.length > L.NOTES_MAX_LENGTH) {
      setAdminNotesError(L.ERROR_NOTES_TOO_LONG);
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Upload the staged files first; abort the whole submit if any fail.
      const attachments = await uploadAllFiles(files);
      if (attachments === null) {
        setToastMessage(C.ERROR_UPLOAD_FAILED);
        return;
      }

      const result = await updateServiceRequestStatus({
        serviceRequestId,
        newStatus: 'completed',
        adminNotes: trimmedNotes,
        notify: true,
        attachments,
      });
      if (result.success) {
        setIsOpen(false);
        resetForm();
        onSuccess?.();
      } else {
        setSubmitError(
          result.error ??
            UPDATE_SERVICE_REQUEST_STATUS_ERROR_MESSAGES.DEFAULT_ERROR
        );
      }
    } catch {
      setSubmitError(
        UPDATE_SERVICE_REQUEST_STATUS_ERROR_MESSAGES.UNEXPECTED_ERROR
      );
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    serviceRequestId,
    adminNotes,
    files,
    uploadAllFiles,
    onSuccess,
    resetForm,
  ]);

  return {
    isOpen,
    open,
    close,
    files,
    onFilesChange,
    toastMessage,
    showToast,
    clearToast,
    adminNotes,
    setAdminNotes,
    adminNotesError,
    isSubmitting,
    submitError,
    submit,
  };
};
